import {
  Alert,
  Box,
  Checkbox,
  FormControlLabel,
  Stack,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import AuthButton from "../components/auth/AuthButton";
import AuthFormContainer from "../components/auth/AuthFormContainer";
import AuthLink from "../components/auth/AuthLink";
import AuthTextField from "../components/auth/AuthTextField";
import { useSignUp } from "../hooks/useAuth";
import { SignUpRequest } from "../types/api";
import { MixpanelEvents, trackEvent } from "../utils/mixpanel";

const SignUpPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const next = searchParams.get("next") ?? "";
  const signUp = useSignUp();
  const [formData, setFormData] = useState<SignUpRequest>({
    email: "",
    username: "",
    first_name: "",
    last_name: "",
    password: "",
    password_confirm: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  useEffect(() => {
    trackEvent(MixpanelEvents.SIGNUP_PAGE_VIEWED, { has_next: Boolean(next) });
  }, [next]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.email) {
      newErrors.email = t("auth.emailRequired");
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t("auth.emailInvalid");
    }

    if (!formData.username) {
      newErrors.username = t("auth.username") + " " + t("common.error");
    }

    if (!formData.password) {
      newErrors.password = t("auth.passwordRequired");
    } else if (formData.password.length < 8) {
      newErrors.password = t("auth.passwordRequired");
    }

    if (formData.password !== formData.password_confirm) {
      newErrors.password_confirm = t("auth.passwordsDoNotMatch");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    trackEvent(MixpanelEvents.SIGNUP_SUBMIT, { has_next: Boolean(next) });
    signUp.mutate(formData);
  };

  return (
    <AuthFormContainer>
      <Box
        component="form"
        onSubmit={handleSubmit}
        noValidate
      >
        <Stack spacing={{ xs: 2, sm: 2.5, md: 3 }}>
          <Stack spacing={{ xs: 0.25, md: 0.5 }}>
            <Typography
              variant="h4"
              fontWeight={700}
              sx={{ fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2.125rem" } }}
            >
              {t("auth.signUp")}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                fontSize: { xs: "0.75rem", md: "0.875rem" }
              }}
            >
              {t("auth.dontHaveAccount")}
            </Typography>
          </Stack>

          {signUp.error && (
            <Alert severity="error" sx={{ py: { xs: 0.5, md: 1 } }}>
              {(signUp.error as any)?.response?.data?.detail ||
                t("common.error")}
            </Alert>
          )}

          <Stack spacing={{ xs: 1.5, md: 2 }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 1.5, sm: 2 }}>
              <AuthTextField
                fullWidth
                type="firstName"
                label={t("auth.firstName")}
                autoComplete="given-name"
                value={formData.first_name}
                onChange={(e) =>
                  setFormData({ ...formData, first_name: e.target.value })
                }
              />
              <AuthTextField
                fullWidth
                type="lastName"
                label={t("auth.lastName")}
                autoComplete="family-name"
                value={formData.last_name}
                onChange={(e) =>
                  setFormData({ ...formData, last_name: e.target.value })
                }
              />
            </Stack>

            <AuthTextField
              fullWidth
              type="username"
              label={t("auth.username")}
              autoComplete="username"
              value={formData.username}
              onChange={(e) =>
                setFormData({ ...formData, username: e.target.value })
              }
              error={!!errors.username}
              helperText={errors.username}
              required
            />

            <AuthTextField
              fullWidth
              type="email"
              label={t("auth.email")}
              autoComplete="email"
              value={formData.email}
              onChange={(e) =>
                setFormData({ ...formData, email: e.target.value })
              }
              error={!!errors.email}
              helperText={errors.email}
              required
            />

            <AuthTextField
              fullWidth
              type="password"
              label={t("auth.password")}
              autoComplete="new-password"
              value={formData.password}
              onChange={(e) =>
                setFormData({ ...formData, password: e.target.value })
              }
              error={!!errors.password}
              helperText={errors.password}
              required
            />
            <AuthTextField
              fullWidth
              type="password"
              label={t("auth.confirmPassword")}
              autoComplete="new-password"
              value={formData.password_confirm}
              onChange={(e) =>
                setFormData({ ...formData, password_confirm: e.target.value })
              }
              error={!!errors.password_confirm}
              helperText={errors.password_confirm}
              required
            />
          </Stack>

          <FormControlLabel
            control={
              <Checkbox
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                sx={{
                  color: "text.secondary",
                  "&.Mui-checked": {
                    color: (theme) => (theme.palette as any).neon?.pink || theme.palette.error.main,
                  },
                }}
              />
            }
            label={
              <Typography
                variant="body2"
                sx={{
                  fontSize: { xs: "0.75rem", md: "0.875rem" },
                  color: "text.secondary",
                }}
              >
                {t("terms.acceptTerms")}{" "}
                <AuthLink to="/terms-and-conditions">
                  {t("terms.termsAndConditions")}
                </AuthLink>
              </Typography>
            }
            sx={{
              alignItems: "flex-start",
              "& .MuiFormControlLabel-label": {
                marginTop: "4px",
              },
            }}
          />

          <AuthButton
            type="submit"
            variant="primary"
            loading={signUp.isPending}
            disabled={!acceptedTerms}
          >
            {t("auth.signUp")}
          </AuthButton>

          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              textAlign: "center",
              fontSize: { xs: "0.75rem", md: "0.875rem" },
              pt: { xs: 0.5, md: 0 }
            }}
          >
            {t("auth.alreadyHaveAccount")}{" "}
            <AuthLink to="/login">
              {t("auth.login")}
            </AuthLink>
          </Typography>
        </Stack>
      </Box>
    </AuthFormContainer>
  );
};

export default SignUpPage;
